// Display-layer formatting helpers — kept dependency-free so the bundle stays
// tiny. Anything that needs locale-aware output uses Intl directly.
//
// Every timestamp here is stored UTC and rendered in the TENANT's timezone (not
// the browser's) — one tenant, one clock, whoever is looking. The zone comes from
// lib/tz.js, which AuthContext primes at bootstrap. Pass `tz` explicitly only to
// override it.

import { formatInZone } from './tz.js';

export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatMinutes(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '0';
  return Math.round(seconds / 60).toLocaleString();
}

export function formatPhone(e164) {
  if (!e164) return '—';
  // Best-effort US/E.164 prettifier — keeps the original on non-match.
  const m = String(e164).match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return e164;
}

export function formatDateTime(iso, tz) {
  if (!iso) return '—';
  return (
    formatInZone(
      iso,
      { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
      tz
    ) || iso
  );
}

// Same, plus the zone label ("Jul 15, 9:10 AM CDT"). For the surfaces where the
// reader is REASONING about a time rather than skimming a column — a schedule
// echo, a single call's own timestamp. Dense tables stay unlabeled; the zone is
// stated once on the page instead.
export function formatDateTimeWithZone(iso, tz) {
  if (!iso) return '—';
  return (
    formatInZone(
      iso,
      {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      },
      tz
    ) || iso
  );
}

export function formatDate(iso, tz) {
  if (!iso) return '—';
  return (
    formatInZone(iso, { year: 'numeric', month: 'short', day: 'numeric' }, tz) || iso
  );
}

export function statusBadge(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return 'bg-emerald-900/40 text-emerald-300 border border-emerald-800';
  if (s === 'failed') return 'bg-rose-900/40 text-rose-300 border border-rose-800';
  if (s === 'in-progress' || s === 'ringing')
    return 'bg-amber-900/40 text-amber-300 border border-amber-800';
  return 'bg-stone-800 text-stone-300 border border-stone-700';
}

// ── E.164 NORMALISATION ──────────────────────────────────────────────────────
// Turn what a person actually types into what the API demands, or return null.
//
// 2026-08-15: the test-call field rejected "+17372728567." — a trailing full stop from
// dictation — and the button simply did nothing while a warning explained the E.164
// format. Refusing a correct number over punctuation is pedantry, not safety.
//
// ⛔ WHAT THIS WILL NOT DO is guess. Stripping spaces, dashes, brackets and stray
// punctuation cannot change WHICH number is dialled. Assuming +1 for a bare 10-digit US
// number is the one inference here, and it is why callers must show the result back
// before placing a call — a normaliser that silently retargets is worse than a strict
// one that refuses.
export function toE164(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  const hadPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return null;
  if (hadPlus) return digits.length >= 7 && digits.length <= 15 ? `+${digits}` : null;
  // No plus: 10 digits is a US/Canada national number; 11 starting with 1 is the same
  // number already carrying its country code.
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  // Anything else is ambiguous without a country — refuse rather than invent one.
  return null;
}
